/**
 * TecDoc Vehicle Selector - Shopify Integration
 * Calls TecDocProxyApi endpoints via Shopify App Proxy (/apps/tecdoc/...)
 * Vehicle selection persisted in localStorage for 30 days.
 */
(function () {
  'use strict';

  // -- Config ----------------------------------------------------------------
  const API_BASE    = '/apps/tecdoc';  // Shopify App Proxy
  const STORAGE_KEY = 'tecdoc_vehicle';
  const EXPIRE_DAYS = 30;

  // -- State -----------------------------------------------------------------
  let _lang         = 'de';
  let _lastVehicles = [];  // cache for imageUrl lookup after vehicle select

  // -- Storage helpers -------------------------------------------------------
  function saveVehicle(vehicle) {
    const payload = { ...vehicle, savedAt: Date.now() };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (_) {}
    document.dispatchEvent(new CustomEvent('tecdoc:vehicle-selected', { detail: payload }));
  }

  function loadVehicle() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const v = JSON.parse(raw);
      const ageDays = (Date.now() - (v.savedAt || 0)) / 86400000;
      if (ageDays > EXPIRE_DAYS) { localStorage.removeItem(STORAGE_KEY); return null; }
      return v;
    } catch (_) { return null; }
  }

  function clearVehicle() {
    localStorage.removeItem(STORAGE_KEY);
    document.dispatchEvent(new CustomEvent('tecdoc:vehicle-cleared'));
  }

  // -- i18n helper (DE / FR / EN fallback) -----------------------------------
  function td(de, fr, en) {
    return de;
  }

  const BRAND_SLUG_ALIASES = Object.freeze({
    'mercedes-benz': 'mercedesbenz',
    'rolls-royce': 'rollsroyce'
  });

  // -- HTML escape -----------------------------------------------------------
  function esc(s) {
    return (s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // -- Collection slug (mirrors C# TecDocBusinessFamilyResolver.ToSlug) ------
  function toCollectionSlug(value) {
    if (!value) return '';
    let s = value.trim()
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    let result = '';
    for (let i = 0; i < s.length; i++) {
      const c = s[i];
      if (/[a-zA-Z0-9]/.test(c)) result += c.toLowerCase();
      else if (' _/,().'.includes(c)) result += '-';
    }
    return result.replace(/-{2,}/g, '-').replace(/^-|-$/g, '');
  }

  function normalizeToken(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function getComparableText(...values) {
    return normalizeToken(values.filter(Boolean).join(' '));
  }

  function getComparableTokens(value) {
    return normalizeToken(value)
      .split(' ')
      .map(token => token.trim())
      .filter(Boolean);
  }

  // -- Route helpers ---------------------------------------------------------
  function getConfigSource() {
    return document.querySelector('[data-tecdoc-config-source="true"]') || document.getElementById('tecdoc-vehicle-results');
  }

  function getRouteSlug() {
    const el = getConfigSource();
    return el ? (el.dataset.routeSlug || '').trim() : '';
  }

  function getResultsPageUrl() {
    const el = getConfigSource();
    return el ? (el.dataset.resultsPageUrl || '').trim() : '';
  }

  function getStoreId() {
    const el = getConfigSource();
    return el ? (el.dataset.storeId || 'injektorhaus').trim() : 'injektorhaus';
  }

  function getFamilyName() {
    const el = getConfigSource();
    return el ? (el.dataset.family || 'Injektor').trim() : 'Injektor';
  }

  function buildCollectionUrl(vehicle) {
    const routeSlug = getRouteSlug();
    if (!routeSlug) return null;
    const makeBaseSlug = toCollectionSlug(vehicle.makeName || '');
    const makeSlug = BRAND_SLUG_ALIASES[makeBaseSlug] || makeBaseSlug;
    const modelBase = (vehicle.modelLabel || vehicle.modelName || '')
      .replace(/\s*\([^)]*\)\s*$/, '')
      .trim();
    const modelSlug = toCollectionSlug(modelBase);
    if (!makeSlug) return `/collections/${routeSlug}`;
    return modelSlug
      ? `/collections/${routeSlug}-${makeSlug}-${modelSlug}`
      : `/collections/${routeSlug}-${makeSlug}`;
  }

  function getRootCollectionUrl() {
    const routeSlug = getRouteSlug() || 'injektoren';
    return `/collections/${routeSlug}`;
  }

  function buildVehicleResultsUrl(vehicle) {
    const basePath = getResultsPageUrl();
    if (!basePath || !vehicle || !vehicle.vehicleId) return null;

    const url = new URL(basePath, window.location.origin);
    url.searchParams.set('vehicleId', String(vehicle.vehicleId));
    url.searchParams.set('storeId', getStoreId());
    url.searchParams.set('family', getFamilyName());

    return `${url.pathname}${url.search}`;
  }

  function findBestVehicleMatch(candidates, selected) {
    if (!Array.isArray(candidates) || !candidates.length || !selected) return null;

    const selectedId = String(selected.vehicleId || selected.id || '');
    const exact = selectedId
      ? candidates.find((candidate) => String(candidate.id || '') === selectedId)
      : null;
    if (exact) return exact;

    const selectedText = normalizeToken([
      selected.makeName,
      selected.modelName,
      selected.modelLabel,
      selected.typeName,
      selected.vehicleName,
      selected.engineCode,
      selected.powerHp,
      selected.powerKw,
      selected.yearFrom,
      selected.yearTo
    ].filter(Boolean).join(' '));

    let best = null;
    let bestScore = 0;

    candidates.forEach((candidate) => {
      let score = 0;
      [
        [candidate.makeName, 18],
        [candidate.modelName, 18],
        [candidate.typeName, 22],
        [candidate.engineCode, 22],
        [candidate.powerHp, 8],
        [candidate.powerKw, 6],
        [candidate.yearFrom, 5],
        [candidate.yearTo, 5]
      ].forEach(([value, weight]) => {
        const token = normalizeToken(value);
        if (token && selectedText.includes(token)) score += weight;
      });

      if (score > bestScore) {
        best = candidate;
        bestScore = score;
      }
    });

    return bestScore >= 40 ? best : null;
  }

  function getPreferredVehicleContext(el) {
    if (!el) return null;
    const context = {
      makeName: el.dataset.vppMake || '',
      modelName: el.dataset.vppModel || '',
      typeName: el.dataset.vppPlatform || '',
      engineCode: el.dataset.vppEngine || '',
      powerKw: el.dataset.vppKw || '',
      powerHp: el.dataset.vppHp || '',
      yearFrom: el.dataset.vppYearFrom || '',
      yearTo: el.dataset.vppYearTo || ''
    };

    return Object.values(context).some(Boolean) ? context : null;
  }

  function findPreferredVehicleMatch(candidates, preferred) {
    if (!Array.isArray(candidates) || !candidates.length || !preferred) return null;

    const preferredText = normalizeToken([
      preferred.makeName,
      preferred.modelName,
      preferred.typeName,
      preferred.engineCode,
      preferred.powerHp,
      preferred.powerKw,
      preferred.yearFrom,
      preferred.yearTo
    ].filter(Boolean).join(' '));

    if (!preferredText) return null;

    let best = null;
    let bestScore = 0;
    const preferredModel = normalizeToken(preferred.modelName);
    const preferredPlatformTokens = getComparableTokens(preferred.typeName).filter(token => /\d/.test(token) || token.length >= 3);

    candidates.forEach((candidate) => {
      const candidateVehicleText = getComparableText(candidate.makeName, candidate.modelName, candidate.typeName);
      const candidateModel = normalizeToken(candidate.modelName);
      if (preferredModel) {
        const modelMatches =
          (candidateModel && (preferredModel.includes(candidateModel) || candidateModel.includes(preferredModel))) ||
          (candidateVehicleText && candidateVehicleText.includes(preferredModel));
        if (!modelMatches) return;
      }

      if (preferredPlatformTokens.length) {
        const platformMatches = preferredPlatformTokens.some((token) => candidateVehicleText.includes(token));
        if (!platformMatches) return;
      }

      let score = 0;
      [
        [candidate.makeName, 24],
        [candidate.modelName, 24],
        [candidate.typeName, 30],
        [candidate.engineCode, 28],
        [candidate.powerHp, 10],
        [candidate.powerKw, 8],
        [candidate.yearFrom, 6],
        [candidate.yearTo, 6]
      ].forEach(([value, weight]) => {
        const token = normalizeToken(value);
        if (token && preferredText.includes(token)) score += weight;
      });

      if (score > bestScore) {
        best = candidate;
        bestScore = score;
      }
    });

    return bestScore >= 52 ? best : null;
  }

  function getBestTargetUrl(vehicle) {
    return buildVehicleResultsUrl(vehicle) || buildCollectionUrl(vehicle);
  }

  function navigateToBestTarget(vehicle) {
    const url = getBestTargetUrl(vehicle);
    if (!url) return;
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (currentUrl === url) return;
    
    window.location.href = url;
  }

  // -- Brand logo helpers ----------------------------------------------------
  function toAssetSlug(value) {
    const slug = (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    return BRAND_SLUG_ALIASES[slug] || slug;
  }

  function getMakeLogo(makeName) {
    const base = window.TECDOC_ASSET_BASE || '';
    const slug = toAssetSlug(makeName);
    return base + 'widget-brand-' + slug + '.svg';
  }

  function makeLogoImg(makeName, cssClass) {
    const src  = getMakeLogo(makeName);
    const init = esc(((makeName || '?').slice(0, 2)).toUpperCase());
    return `<img src="${esc(src)}" class="${esc(cssClass)}" alt="${esc(makeName)}" loading="lazy"
      onerror="this.outerHTML='<span class=\\'${esc(cssClass)} ${esc(cssClass)}--placeholder\\'>${init}</span>'">`;
  }

  function toVendorSlug(vendor) {
    const slug = (vendor || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/\//g, '-')
      .replace(/\./g, '');

    const aliases = {
      'vdo-siemens': 'siemens-vdo'
    };

    return aliases[slug] || slug;
  }

  function renderVendorBadge(vendor) {
    if (!vendor) return '';
    const base = window.TECDOC_THEME_ASSET_BASE || '';
    if (!base) return '';
    const vendorSlug = toVendorSlug(vendor);
    const src = `${base}td-brand-${vendorSlug}.svg`;
    return `<div class="td-brand-badge">
      <img src="${esc(src)}" alt="${esc(vendor)}" width="68" height="28" loading="lazy"
        onerror="this.style.display='none';this.nextElementSibling.style.display='block'">
      <span style="display:none">${esc(vendor)}</span>
    </div>`;
  }

  // -- API helpers -----------------------------------------------------------
  async function apiFetch(path) {
    const res = await fetch(`${API_BASE}${path}`);
    let json = null;
    try {
      json = await res.json();
    } catch (_) {
      json = null;
    }
    if (!res.ok) {
      const message = json && json.error
        ? json.error
        : json && json.success === false && json.error
          ? json.error
          : `API error ${res.status}`;
      throw new Error(message);
    }
    if (!json) throw new Error('Leere API-Antwort');
    if (!json.success) throw new Error(json.error || 'API error');
    return json.data;
  }

  async function searchPlate(plate, country, storeId, family) {
    const params = new URLSearchParams({
      plate: plate || '',
      country: country || 'FR',
      lang: _lang,
      storeId: storeId || getStoreId(),
      family: family || getFamilyName()
    });
    return apiFetch(`/plate?${params.toString()}`);
  }
  async function getMakes(storeId, family) {
    const params = new URLSearchParams({
      lang: _lang,
      storeId: storeId || getStoreId(),
      family: family || getFamilyName()
    });
    return apiFetch(`/makes?${params.toString()}`);
  }
  async function getModels(makeId, storeId, family) {
    const params = new URLSearchParams({
      makeId: String(makeId),
      lang: _lang,
      storeId: storeId || getStoreId(),
      family: family || getFamilyName()
    });
    return apiFetch(`/models?${params.toString()}`);
  }
  async function getVehicles(makeId, modelSeriesId, storeId, family) {
    const params = new URLSearchParams({
      makeId: String(makeId),
      modelSeriesId: String(modelSeriesId),
      lang: _lang,
      storeId: storeId || getStoreId(),
      family: family || getFamilyName()
    });
    return apiFetch(`/vehicles?${params.toString()}`);
  }
  async function checkCompatibility(vehicleId, sku) { return apiFetch(`/compatibility?vehicleId=${vehicleId}&sku=${encodeURIComponent(sku)}`); }
  async function search(q)                          { return apiFetch(`/search?q=${encodeURIComponent(q)}&lang=${_lang}`); }
  async function searchHsnTsn(hsn, tsn, storeId, family) {
    const params = new URLSearchParams({
      hsn: hsn || '',
      tsn: tsn || '',
      lang: _lang,
      storeId: storeId || getStoreId(),
      family: family || getFamilyName()
    });
    return apiFetch(`/hsn-tsn?${params.toString()}`);
  }
  async function getArticleVehicles(sku)            { return apiFetch(`/article-vehicles?sku=${encodeURIComponent(sku)}&lang=${_lang}`); }
  async function getCompatibleSkus(vehicleId)       { return apiFetch(`/compatible-skus?vehicleId=${encodeURIComponent(vehicleId)}`); }
  async function getVehicleResults(vehicleId, storeId, family, limit, page) {
    const params = new URLSearchParams({
      vehicleId: String(vehicleId),
      storeId: storeId || getStoreId(),
      family: family || getFamilyName(),
      limit: String(limit || 48),
      page: String(page || 1)
    });
    return apiFetch(`/vehicle-results?${params.toString()}`);
  }

  // -- Combobox --------------------------------------------------------------
  class TecDocCombobox {
    constructor(container) {
      this._placeholder = container.dataset.label || '';
      this._items  = [];
      this._value  = '';
      this._label  = '';

      this._wrap   = document.createElement('div');
      this._wrap.className = 'tecdoc-cb-wrap tecdoc-cb--disabled';

      this._input  = document.createElement('input');
      this._input.type = 'text';
      this._input.className = 'tecdoc-cb-input';
      this._input.placeholder = this._placeholder;
      this._input.disabled = true;
      this._input.autocomplete = 'off';

      this._list   = document.createElement('ul');
      this._list.className = 'tecdoc-cb-list tecdoc-hidden';

      this._wrap.appendChild(this._input);
      this._wrap.appendChild(this._list);
      container.appendChild(this._wrap);

      this._input.addEventListener('input', () => this._filter());
      this._input.addEventListener('focus', (e) => { if (!this._input.disabled) { e.target.scrollIntoView({ block: 'nearest', behavior: 'instant' }); this._open(); } });
      this._input.addEventListener('blur',  () => setTimeout(() => this._close(), 150));
    }

    get value() { return this._value; }

    populate(items, idKey, labelFn) {
      this._items = items.map(item => ({
        id:    String(item[idKey]),
        label: typeof labelFn === 'function' ? labelFn(item) : item[labelFn]
      }));
      this._value = '';
      this._label = '';
      this._input.value = '';
      this._input.disabled = false;
      this._wrap.classList.remove('tecdoc-cb--disabled');
      this._renderList(this._items);
    }

    reset() {
      this._items  = [];
      this._value  = '';
      this._label  = '';
      this._input.value = '';
      this._input.placeholder = this._placeholder;
      this._input.disabled = true;
      this._wrap.classList.add('tecdoc-cb--disabled');
      this._list.innerHTML = '';
      this._list.classList.add('tecdoc-hidden');
    }

    setLoading() {
      this._input.disabled = true;
      this._input.value = '';
      this._input.placeholder = td('Laden...', 'Chargement...', 'Loading...');
      this._wrap.classList.add('tecdoc-cb--disabled');
    }

    setValue(id, label) {
      this._value = String(id);
      this._label = label;
      this._input.value = label;
    }

    _filter() {
      const q = this._input.value.toLowerCase();
      const filtered = q
        ? this._items.filter(i => i.label.toLowerCase().includes(q))
        : this._items;
      this._renderList(filtered);
      this._open();
    }

    _renderList(items) {
      this._list.innerHTML = items.slice(0, 200).map(i =>
        `<li class="tecdoc-cb-item" data-id="${esc(i.id)}">${esc(i.label)}</li>`
      ).join('');
      this._list.querySelectorAll('.tecdoc-cb-item').forEach(li => {
        li.addEventListener('mousedown', e => {
          e.preventDefault();
          this._select(li.dataset.id, li.textContent);
        });
      });
    }

    _select(id, label) {
      this._value = id;
      this._label = label;
      this._input.value = label;
      this._close();
      this._input.dispatchEvent(new CustomEvent('cb:change', { bubbles: true }));
    }

    _open()  {
      if (!this._items.length) return;
      this._list.classList.remove('tecdoc-hidden');
      // flip upward if not enough space below
      const rect = this._wrap.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 260 && rect.top > 260) {
        this._list.style.top    = 'auto';
        this._list.style.bottom = 'calc(100% + 4px)';
      } else {
        this._list.style.top    = 'calc(100% + 4px)';
        this._list.style.bottom = 'auto';
      }
    }
    _close() { this._list.classList.add('tecdoc-hidden'); }
  }

  // -- Tab switcher ----------------------------------------------------------
  function initTabs() {
    const tabs = document.querySelectorAll('.tecdoc-tab');
    if (!tabs.length) return;
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => t.classList.remove('tecdoc-tab--active'));
        tab.classList.add('tecdoc-tab--active');
        const panelId = 'tecdoc-tab-' + tab.dataset.tab;
        document.querySelectorAll('.tecdoc-tab-panel').forEach(p => p.classList.add('tecdoc-hidden'));
        const panel = document.getElementById(panelId);
        if (panel) panel.classList.remove('tecdoc-hidden');
      });
    });
  }

  // -- HSN/TSN search --------------------------------------------------------
  function initHsnTsn() {
    const btnSearch  = document.getElementById('tecdoc-hsn-search');
    const inputHsn   = document.getElementById('tecdoc-hsn-input');
    const inputTsn   = document.getElementById('tecdoc-tsn-input');
    const resultDiv  = document.getElementById('tecdoc-hsn-result');
    const vehicleDiv = document.getElementById('tecdoc-hsn-vehicles');
    if (!btnSearch || !inputHsn || !inputTsn) return;

    async function doSearch() {
      const hsn = inputHsn.value.trim().toUpperCase();
      const tsn = inputTsn.value.trim().toUpperCase();
      if (!hsn || !tsn) return;

      if (resultDiv) { resultDiv.className = 'tecdoc-hsn-result tecdoc-hsn-result--loading'; resultDiv.textContent = '...'; }
      if (vehicleDiv) vehicleDiv.innerHTML = '';

      try {
        const vehicles = await searchHsnTsn(hsn, tsn, getStoreId(), getFamilyName());
        if (!vehicles || vehicles.length === 0) {
          if (resultDiv) { resultDiv.className = 'tecdoc-hsn-result tecdoc-hsn-result--error'; resultDiv.textContent = td('Kein Fahrzeug gefunden.', 'Aucun véhicule trouvé.', 'No vehicle found.'); }
          return;
        }
        if (resultDiv) { resultDiv.className = 'tecdoc-hsn-result tecdoc-hsn-result--ok'; resultDiv.textContent = td(`${vehicles.length} Fahrzeug(e) gefunden`, `${vehicles.length} véhicule(s) trouvé(s)`, `${vehicles.length} vehicle(s) found`); }
        if (vehicleDiv) {
          vehicleDiv.innerHTML = vehicles.map((v, i) => renderHsnVehicleItem(v, i)).join('');
          vehicleDiv.querySelectorAll('.tecdoc-hsn-vehicle-item').forEach(el => {
            el.addEventListener('click', () => {
              const v = vehicles[parseInt(el.dataset.idx)];
              const saved = {
                ...v,
                vehicleId:   v.id,
                modelId:     v.modelSeriesId,
                vehicleName: [v.typeName, v.powerHp, v.yearFrom ? v.yearFrom+'-'+(v.yearTo||'>') : ''].filter(Boolean).join(' '),
              };
              saveVehicle(saved);
              updateFinder();
              navigateToBestTarget(saved);
            });
          });
        }
      } catch (e) {
        if (resultDiv) { resultDiv.className = 'tecdoc-hsn-result tecdoc-hsn-result--error'; resultDiv.textContent = e.message; }
      }
    }

    btnSearch.addEventListener('click', doSearch);
    [inputHsn, inputTsn].forEach(inp => inp.addEventListener('keydown', e => { if (e.key === 'Enter') doSearch(); }));
  }

  // -- FR plate search -------------------------------------------------------
  function initPlateFr() {
    const btn       = document.getElementById('tecdoc-plate-search');
    const input     = document.getElementById('tecdoc-plate-input');
    const resultDiv = document.getElementById('tecdoc-plate-result');
    const listDiv   = document.getElementById('tecdoc-plate-vehicles');
    if (!btn || !input) return;

    async function doSearch() {
      const plate = input.value.trim().toUpperCase().replace(/[\s-]/g, '');
      if (plate.length < 4) return;
      if (resultDiv) { resultDiv.className = 'tecdoc-hsn-result tecdoc-hsn-result--loading'; resultDiv.textContent = '...'; }
      if (listDiv) listDiv.innerHTML = '';
      try {
        const vehicles = await searchPlate(plate, 'FR', getStoreId(), getFamilyName());
        if (!vehicles || !vehicles.length) {
          if (resultDiv) { resultDiv.className = 'tecdoc-hsn-result tecdoc-hsn-result--error'; resultDiv.textContent = 'Aucun véhicule trouvé.'; }
          return;
        }
        if (resultDiv) { resultDiv.className = 'tecdoc-hsn-result tecdoc-hsn-result--ok'; resultDiv.textContent = vehicles.length + ' véhicule(s) trouvé(s)'; }
        if (listDiv) {
          listDiv.innerHTML = vehicles.map((v, i) => renderHsnVehicleItem(v, i)).join('');
          listDiv.querySelectorAll('.tecdoc-hsn-vehicle-item').forEach(el => {
            el.addEventListener('click', () => {
              const v = vehicles[parseInt(el.dataset.idx)];
              const saved = {
                ...v,
                vehicleId:   v.id,
                modelId:     v.modelSeriesId,
                vehicleName: [v.typeName, v.powerHp, v.yearFrom ? v.yearFrom+'-'+(v.yearTo||'>') : ''].filter(Boolean).join(' '),
              };
              saveVehicle(saved);
              updateFinder();
              navigateToBestTarget(saved);
            });
          });
        }
      } catch (e) {
        if (resultDiv) { resultDiv.className = 'tecdoc-hsn-result tecdoc-hsn-result--error'; resultDiv.textContent = e.message; }
      }
    }

    btn.addEventListener('click', doSearch);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') doSearch(); });
  }

  function renderHsnVehicleItem(v, idx) {
    const img     = v.imageUrl ? `<img src="${API_BASE}/vehicle-image?url=${encodeURIComponent(v.imageUrl)}" alt="" class="tecdoc-hsn-vehicle-img" loading="lazy" />` : '<div class="tecdoc-hsn-vehicle-img tecdoc-hsn-vehicle-img--placeholder"></div>';
    const name    = esc(`${v.makeName||''} ${v.modelName||''} ${v.typeName||''}`.trim());
    const details = esc([v.powerKw && (v.powerKw + ''), v.powerHp && (v.powerHp + ''), v.fuelType, (v.yearFrom||'') + (v.yearTo ? ' - '+v.yearTo : '')].filter(Boolean).join(' / '));
    return `<div class="tecdoc-hsn-vehicle-item" data-idx="${idx}" role="button" tabindex="0">${img}<div class="tecdoc-hsn-vehicle-info"><strong>${name}</strong><span>${details}</span></div></div>`;
  }

  // -- Finder UI -------------------------------------------------------------
  function initSelector() {
    const elMake    = document.getElementById('tecdoc-make');
    const elModel   = document.getElementById('tecdoc-model');
    const elVehicle = document.getElementById('tecdoc-vehicle');
    const btnConfirm = document.getElementById('tecdoc-confirm');
    const btnClear   = document.getElementById('tecdoc-clear');

    if (!elMake) return;

    const cbMake    = new TecDocCombobox(elMake);
    const cbModel   = new TecDocCombobox(elModel);
    const cbVehicle = new TecDocCombobox(elVehicle);

    initTabs();
    initHsnTsn();
    initPlateFr();

    async function loadMakes() {
      cbMake.setLoading();
      try {
        const makes = await getMakes(getStoreId(), getFamilyName());
        cbMake.populate(makes, 'id', 'name');
        const saved = loadVehicle();
        if (saved?.makeId) {
          const m = makes.find(x => x.id === saved.makeId);
          if (m) { cbMake.setValue(m.id, m.name); await onMakeSelected(saved); }
        }
      } catch (e) { console.error(e); cbMake.reset(); }
    }

    async function onMakeSelected(saved) {
      cbModel.reset();
      cbVehicle.reset();
      if (btnConfirm) btnConfirm.disabled = true;
      const makeId = parseInt(cbMake.value);
      if (!makeId) return;
      cbModel.setLoading();
      try {
        const models = await getModels(makeId, getStoreId(), getFamilyName());
        cbModel.populate(models, 'id', 'name');
        if (saved?.makeId === makeId && saved?.modelId) {
          const mo = models.find(x => x.id === saved.modelId);
          if (mo) { cbModel.setValue(mo.id, mo.name); await onModelSelected(saved); }
        }
      } catch (e) { console.error(e); cbModel.reset(); }
    }

    async function onModelSelected(saved) {
      cbVehicle.reset();
      if (btnConfirm) btnConfirm.disabled = true;
      const makeId  = parseInt(cbMake.value);
      const modelId = parseInt(cbModel.value);
      if (!makeId || !modelId) return;
      cbVehicle.setLoading();
      try {
        const vehicles = await getVehicles(makeId, modelId, getStoreId(), getFamilyName());
        _lastVehicles = vehicles || [];
        cbVehicle.populate(vehicles, 'id',
          v => [v.typeName, v.engineCode, v.powerHp && (v.powerHp + ''), v.yearFrom + '-' + (v.yearTo || '>')].filter(Boolean).join(' '));
        if (saved?.modelId === modelId && saved?.vehicleId) {
          const ve = vehicles.find(x => x.id === saved.vehicleId);
          if (ve) {
            const label = [ve.typeName, ve.engineCode, ve.powerHp && (ve.powerHp + ''), ve.yearFrom + '-' + (ve.yearTo || '>')].filter(Boolean).join(' ');
            cbVehicle.setValue(ve.id, label);
            if (btnConfirm) btnConfirm.disabled = false;
          }
        }
      } catch (e) { console.error(e); cbVehicle.reset(); }
    }

    elMake.addEventListener('cb:change',    () => onMakeSelected(null));
    elModel.addEventListener('cb:change',   () => onModelSelected(null));
    elVehicle.addEventListener('cb:change', () => { if (btnConfirm) btnConfirm.disabled = !cbVehicle.value; });

    btnConfirm?.addEventListener('click', () => {
      const vid = parseInt(cbVehicle.value);
      if (!vid) return;
      const v = _lastVehicles.find(v => v.id === vid);
      const saved = {
        ...(v || {}),
        vehicleId:   vid,
        makeId:      parseInt(cbMake.value),
        modelId:     parseInt(cbModel.value),
        makeName:    v?.makeName  || cbMake.value,
        modelName:   v?.modelName || cbModel._label || cbModel.value,
        modelLabel:  cbModel._label || v?.modelName || '',
        vehicleName: cbVehicle._label,
        imageUrl:    v?.imageUrl || '',
      };
      saveVehicle(saved);
      updateFinder();
      navigateToBestTarget(saved);
    });

    btnClear?.addEventListener('click', () => {
      clearVehicle();
      _lastVehicles = [];
      cbMake.reset();
      cbModel.reset();
      cbVehicle.reset();
      if (btnConfirm) btnConfirm.disabled = true;
      loadMakes();
      updateFinder();
    });

    loadMakes();
    updateFinder();
  }

  // -- updateFinder ----------------------------------------------------------
  function updateFinder() {
    const vehicle    = loadVehicle();
    const finderForm = document.getElementById('tecdoc-finder-form');
    const finderSel  = document.getElementById('tecdoc-finder-selected');
    const barText    = document.getElementById('tecdoc-bar-text');
    const vehicleImg = document.getElementById('tecdoc-vehicle-img');
    const catalogLink = document.getElementById('tecdoc-catalog-link');

    if (finderForm && finderSel) {
      if (vehicle) {
        finderForm.classList.add('tecdoc-hidden');
        finderSel.classList.remove('tecdoc-hidden');
        if (barText) barText.textContent = vehicle.makeName + ' ' + (vehicle.modelLabel || vehicle.modelName) + ' - ' + vehicle.vehicleName;
        if (vehicleImg) {
          if (vehicle.imageUrl) {
            vehicleImg.src = vehicle.imageUrl;
            vehicleImg.alt = esc(vehicle.makeName + ' ' + vehicle.modelName);
            vehicleImg.classList.remove('tecdoc-hidden');
          } else {
            vehicleImg.classList.add('tecdoc-hidden');
          }
        }
        if (catalogLink) {
          const url = getBestTargetUrl(vehicle);
          if (url) {
            catalogLink.href = url;
            catalogLink.removeAttribute('aria-disabled');
            catalogLink.classList.remove('tecdoc-hidden');
          } else {
            catalogLink.removeAttribute('href');
            catalogLink.setAttribute('aria-disabled', 'true');
            catalogLink.classList.add('tecdoc-hidden');
          }
        }
      } else {
        finderForm.classList.remove('tecdoc-hidden');
        finderSel.classList.add('tecdoc-hidden');
        if (catalogLink) {
          catalogLink.removeAttribute('href');
          catalogLink.setAttribute('aria-disabled', 'true');
          catalogLink.classList.add('tecdoc-hidden');
        }
      }
    }

    initCompatibility();
  }

  function updateBar() { updateFinder(); }

  function normalizeSkuCore(value) {
    return (value || '').split('-')[0].split('_')[0].trim().toUpperCase();
  }

  // -- Compatibility badge updates all .tecdoc-compat-badge[data-sku] elements --
  async function initCompatibility() {
    const badges = document.querySelectorAll('.tecdoc-compat-badge[data-sku]');
    if (!badges.length) return;

    const src    = badges[0];
    const rawSku = src.dataset.sku;
    const sku    = normalizeSkuCore(rawSku);
    const vehicle = loadVehicle();

    if (!sku || !(vehicle?.vehicleId || vehicle?.id)) {
      badges.forEach(b => {
        b.innerHTML = compatHtml('none', b.dataset.labelSelect || td('Fahrzeug auswaehlen, um Kompatibilitaet zu pruefen', 'Selectionnez un vehicule pour verifier la compatibilite', 'Select a vehicle to check compatibility'));
      });
      return;
    }

    badges.forEach(b => { b.innerHTML = compatHtml('loading', '...'); });
    try {
      const result = await getCompatibleSkus(vehicle.vehicleId || vehicle.id);
      const compatibleSkus = new Set((result?.skus || []).map(normalizeSkuCore));
      const isCompatible = compatibleSkus.has(sku);
      badges.forEach(b => {
        if (isCompatible) {
          const tpl = b.dataset.labelOk || td('Kompatibel mit {vehicle}', 'Compatible avec {vehicle}', 'Compatible with {vehicle}');
          b.innerHTML = compatHtml('ok', tpl.replace('{vehicle}', vehicle.vehicleName || vehicle.makeName));
        } else {
          b.innerHTML = compatHtml('fail', b.dataset.labelFail || td('Nicht kompatibel mit Ihrem Fahrzeug', 'Non compatible avec votre véhicule', 'Not compatible with your vehicle'));
        }
      });
    } catch (_) {
      badges.forEach(b => {
        b.innerHTML = compatHtml('warn', b.dataset.labelWarn || td('Kompatibilitaet konnte nicht bestaetigt werden', 'Compatibilite non confirmee', 'Compatibility could not be confirmed'));
      });
    }
  }

  function compatHtml(type, text) {
    const icons = {
      ok:      '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><circle cx="6.5" cy="6.5" r="6" fill="currentColor" opacity=".18"/><path d="M3.5 6.5l2 2 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      fail:    '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><circle cx="6.5" cy="6.5" r="6" fill="currentColor" opacity=".18"/><path d="M4 4l5 5M9 4l-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
      warn:    '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><circle cx="6.5" cy="6.5" r="6" fill="currentColor" opacity=".18"/><path d="M6.5 4v3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="6.5" cy="9.5" r=".7" fill="currentColor"/></svg>',
      loading: '',
      none:    '',
    };
    const styles = {
      ok:      'color:rgb(var(--color-foreground));background:rgba(var(--color-foreground),.035);border-color:rgba(var(--color-foreground),.12)',
      fail:    'color:rgb(var(--color-foreground));background:rgba(var(--color-foreground),.035);border-color:rgba(var(--color-foreground),.12)',
      warn:    'color:rgb(var(--color-foreground));background:rgba(var(--color-foreground),.035);border-color:rgba(var(--color-foreground),.12)',
      loading: 'color:rgba(var(--color-foreground),.68);background:rgba(var(--color-foreground),.035);border-color:rgba(var(--color-foreground),.12)',
      none:    'color:rgba(var(--color-foreground),.68);background:rgba(var(--color-foreground),.035);border-color:rgba(var(--color-foreground),.12)',
    };
    return `<span style="display:inline-flex;align-items:center;gap:5px;padding:5px 11px;border-radius:99px;border:1px solid;font-size:1.3rem;font-weight:600;line-height:1.3;${styles[type]}">${icons[type]}${text}</span>`;
  }

  // -- Widget 1: compatible vehicle card (updates on vehicle select) ----------
  async function initWidgetVehicle() {
    const el = document.getElementById('tecdoc-widget-vehicle');
    if (!el) return;
    const articleNumber = (el.dataset.sku || '').split('-')[0].split('_')[0].trim();
    if (!articleNumber) return;
    const preferredVehicle = getPreferredVehicleContext(el);

    let _allVehicles = [];
    let _totalVehicles = 0;

    function renderW1(v, total, displayVehicle) {
      const display = displayVehicle || v;
      const displayImageUrl = display.imageUrl || v.imageUrl || '';
      const imgSrc = displayImageUrl
        ? `${API_BASE}/vehicle-image?url=${encodeURIComponent(displayImageUrl)}`
        : '';

      const lDisp  = td('Hubraum',    'Cylindrée',    'Displacement');
      const lPow   = td('Leistung',   'Puissance',    'Power');
      const lFuel  = td('Kraftstoff', 'Carburant',    'Fuel');
      const lEng   = td('Motorcode',  'Code moteur',  'Engine code');
      const lBody  = td('Aufbau',     'Carrosserie',  'Body');
      const lDrive = td('Antrieb',    'Transmission', 'Drive');
      const lGear  = td('Getriebe',   'Boite',        'Gearbox');
      const lYear  = td('Baujahr',    'Année',        'Year');
      const lCyl   = td('Zylinder',   'Cylindres',    'Cylinders');
      const lDoors = td('Türen',      'Portes',       'Doors');
      const lType  = td('Typ',        'Type',         'Type');

      const power    = [display.powerKw || v.powerKw, display.powerHp || v.powerHp].filter(Boolean).join(' / ');
      const years    = [display.yearFrom || v.yearFrom, display.yearTo || v.yearTo].filter(Boolean).join(' - ');
      const headline = [display.makeName || v.makeName, display.modelLabel || display.modelName || v.modelName].filter(Boolean).join(' ');
      const subline  = [display.typeName || display.vehicleName || v.typeName, power, years].filter(Boolean).join(' - ');

      const specs = [
        [lType,  v.typeName],
        [lDisp,  v.displacement || v.capacityLiters],
        [lPow,   power],
        [lFuel,  v.fuelType],
        [lEng,   v.engineCode],
        [lBody,  v.bodyStyle],
        [lDrive, v.driveType],
        [lGear,  v.transmission],
        [lYear,  years],
        [lCyl,   v.cylinders > 0 ? String(v.cylinders) : ''],
        [lDoors, v.doors > 0 ? String(v.doors) : ''],
      ].filter(([, val]) => val);

      const specRows = specs.map(([la, va]) => `
        <tr>
          <td class="tecdoc-td-label">${esc(la)}</td>
          <td class="tecdoc-td-val">${esc(va)}</td>
        </tr>`).join('');

      const remaining = total - 1;
      const more = remaining > 0
        ? `<div class="tecdoc-w1-more">+ ${remaining} ${td('weitere Fahrzeuge', 'véhicules de plus', 'more vehicles')}</div>`
        : '';

      const catalogBtn = '';

      el.innerHTML = `
        <div class="tecdoc-w1">
          ${imgSrc ? `<img src="${esc(imgSrc)}" class="tecdoc-w1-photo" alt="${esc(headline)}" loading="lazy">` : ''}
          <div class="tecdoc-w1-head">
            <span class="tecdoc-w1-make">${esc(headline)}</span>
            ${subline ? `<span class="tecdoc-w1-sub">${esc(subline)}</span>` : ''}
          </div>
          <div class="tecdoc-w1-body">
            ${specRows ? `<table class="tecdoc-mg-table tecdoc-mg-table--specs"><thead><tr><th>${td('Eigenschaft','Propriété','Property')}</th><th>${td('Wert','Valeur','Value')}</th></tr></thead><tbody>${specRows}</tbody></table>` : ''}
          </div>
          ${more}
          ${catalogBtn}
        </div>`;
    }

    function onVehicleSelected(e) {
      if (!_allVehicles.length) return;
      if (preferredVehicle) {
        const preferredMatch = findPreferredVehicleMatch(_allVehicles, preferredVehicle);
        if (preferredMatch) {
          renderW1(preferredMatch, _totalVehicles, preferredVehicle);
          return;
        }
      }
      const selected = e.detail;
      const match = findBestVehicleMatch(_allVehicles, selected);
      if (match) {
        renderW1(match, _totalVehicles, selected);
      } else {
        // Selected vehicle is not compatible with this product.
        el.innerHTML = `<div class="tecdoc-w1 tecdoc-w1--incompatible">
          <span>${td('Dieses Produkt ist nicht kompatibel mit Ihrem Fahrzeug.', 'Ce produit n\'est pas compatible avec votre véhicule.', 'This product is not compatible with your vehicle.')}</span>
        </div>`;
      }
    }

    function onVehicleCleared() {
      if (!_allVehicles.length) return;
      const preferredMatch = preferredVehicle
        ? findPreferredVehicleMatch(_allVehicles, preferredVehicle)
        : null;
      renderW1(preferredMatch || _allVehicles[0], _totalVehicles, preferredVehicle || null);
    }

    try {
      const result = await getArticleVehicles(articleNumber);
      if (!result || !result.vehicles || !result.vehicles.length) return;
      _allVehicles   = result.vehicles;
      _totalVehicles = result.totalVehicles;

      // On load show the selected vehicle, otherwise show the first compatible vehicle.
      const saved = loadVehicle();
      const preferredMatch = preferredVehicle
        ? findPreferredVehicleMatch(_allVehicles, preferredVehicle)
        : null;
      const savedMatch = (saved?.vehicleId || saved?.vehicleName)
        ? (findBestVehicleMatch(_allVehicles, saved) || _allVehicles[0])
        : null;
      const initial = preferredMatch || savedMatch || _allVehicles[0];
      const initialDisplay = preferredMatch
        ? preferredVehicle
        : (savedMatch ? saved : null);
      renderW1(initial, _totalVehicles, initialDisplay);

      document.addEventListener('tecdoc:vehicle-selected', onVehicleSelected);
      document.addEventListener('tecdoc:vehicle-cleared',  onVehicleCleared);
    } catch (e) { console.error('TecDoc widget-vehicle:', e); }
  }

  // -- Widget 2 & 3: part info + reference numbers ---------------------------
  async function initWidgetPartAndRefs() {
    const elPart = document.getElementById('tecdoc-widget-part');
    const elRefs = document.getElementById('tecdoc-widget-refs');
    if (!elPart && !elRefs) return;
    const articleNumber = ((elPart || elRefs).dataset.sku || '').split('-')[0].split('_')[0].trim();
    if (!articleNumber) return;
    try {
      const result = await search(articleNumber);
      if (!result || !result.length) return;
      const art = result[0];

      // Widget 2: OEM + Vergleichsnummern - table format
      if (elPart) {
        const lOem   = td('OEM-Referenzen',    'Références OEM',      'OEM References');
        const lCmp   = td('Vergleichsnummern', 'Numéros comparables', 'Comparable Numbers');
        const lBrand = td('Marke',             'Marque',              'Brand');
        const lArtNo = td('Artikelnummer',     'Référence',           'Part Number');

        function refTable(items, mod) {
          if (!items || !items.length) return '';
          const lBrandH = td('Marke', 'Marque', 'Brand');
          const lNumH   = td('Artikelnummer', 'Référence', 'Part No.');
          const rows = items.slice(0, 30).map(o => `
            <tr>
              <td class="tecdoc-td-label">${o.brand ? esc(o.brand) : '-'}</td>
              <td class="tecdoc-td-val tecdoc-td-mono">${esc(o.articleNumber)}</td>
            </tr>`).join('');
          return `<div class="tecdoc-mg-wrap tecdoc-mg-wrap--refs"><table class="tecdoc-mg-table tecdoc-mg-table--refs"><thead><tr><th>${lBrandH}</th><th>${lNumH}</th></tr></thead><tbody>${rows}</tbody></table></div>`;
        }

        const oemTable = refTable(art.oemNumbers,       'oem');
        const cmpTable = refTable(art.comparableNumbers,'cmp');

        let html = '';
        if (oemTable) html += `<div class="tecdoc-ref-section"><div class="tecdoc-ref-title tecdoc-ref-title--oem">${lOem}</div>${oemTable}</div>`;
        if (cmpTable) html += `<div class="tecdoc-ref-section"><div class="tecdoc-ref-title tecdoc-ref-title--cmp">${lCmp}</div>${cmpTable}</div>`;

        if (html) elPart.innerHTML = `<div class="tecdoc-w2">${html}</div>`;
      }

      if (elRefs) elRefs.innerHTML = '';

    } catch (e) { console.error('TecDoc widget-part-refs:', e); }
  }

  // -- Article vehicles table ------------------------------------------------
  async function initArticleVehicles() {
    const container = document.getElementById('tecdoc-article-vehicles');
    if (!container) return;
    const sku = container.dataset.sku;
    if (!sku) return;

    // Shopify SKU: "445110273-1-50" -> TecDoc article number: "445110273"
    const articleNumber = sku.split('-')[0].split('_')[0].trim();
    if (!articleNumber) return;

    try {
      const result = await getArticleVehicles(articleNumber);
      if (!result || !result.vehicles || result.vehicles.length === 0) return;
      container.innerHTML = renderArticleVehiclesTable(result, container);

        const groupsEl = container.querySelector('.tecdoc-make-groups');
      if (groupsEl) {
        const lShowAll  = groupsEl.dataset.labelShow  || td('Alle anzeigen', 'Tout afficher', 'Show all');
        const lCollapse = groupsEl.dataset.labelHide  || td('Weniger anzeigen', 'Réduire', 'Show less');
        groupsEl.addEventListener('click', e => {
          const btn = e.target.closest('.tecdoc-mg-toggle');
          if (!btn) return;
          e.preventDefault();
          e.stopPropagation();
          const tbody = btn.closest('.tecdoc-make-group')?.querySelector('tbody');
          if (!tbody) return;
          const hidden = tbody.querySelectorAll('.tecdoc-mg-row--hidden');
          const count  = parseInt(btn.dataset.count || '0', 10);
          if (hidden.length) {
            hidden.forEach(r => r.classList.remove('tecdoc-mg-row--hidden'));
            btn.textContent = lCollapse;
          } else {
            Array.from(tbody.rows).slice(5).forEach(r => r.classList.add('tecdoc-mg-row--hidden'));
            btn.textContent = `${lShowAll} (${count})`;
          }
        });
      }
    } catch (e) { console.error('TecDoc article-vehicles:', e); }
  }

  function renderArticleVehiclesTable(result, container) {
    const lCompat  = (container && container.dataset.labelVehicles) || td('kompatible Fahrzeuge', 'véhicules compatibles', 'compatible vehicles');
    const lEngine  = (container && container.dataset.labelEngine)   || td('Motor',  'Moteur',   'Engine');
    const lYear    = (container && container.dataset.labelYear)      || td('Baujahr','Année',    'Year');
    const lShowAll = td('Alle anzeigen', 'Tout afficher', 'Show all');
    const lCollapse = td('Weniger anzeigen', 'Réduire', 'Show less');
    const PREVIEW  = 5; // rows shown per make group initially
    const groups = {};
    (result.vehicles || []).forEach(v => {
      const key = v.makeName || '?';
      if (!groups[key]) groups[key] = { vehicles: [] };
      groups[key].vehicles.push(v);
    });

    const groupHtml = Object.entries(groups).map(([make, g]) => {
      const makeImg = makeLogoImg(make, 'tecdoc-mg-img');

      const rows = g.vehicles.map((v, i) => {
        const engine = [
          v.displacement || v.capacityLiters,
          v.powerKw && v.powerHp ? `${v.powerKw} / ${v.powerHp}` : (v.powerKw || v.powerHp || ''),
          v.fuelType,
          v.engineCode
        ].filter(Boolean).map(esc).join(' - ');
        const years = [v.yearFrom, v.yearTo].filter(Boolean).join(' - ');
        const hidden = i >= PREVIEW ? ' class="tecdoc-mg-row--hidden"' : '';
        return `<tr${hidden}>
          <td>${esc([v.modelName, v.typeName].filter(Boolean).join(' '))}</td>
          <td>${engine}</td>
          <td>${esc(years)}</td>
        </tr>`;
      }).join('');

      const hasMore = g.vehicles.length > PREVIEW;
      const moreBtn = hasMore
        ? `<button type="button" class="tecdoc-mg-toggle" data-count="${g.vehicles.length - PREVIEW}">
             ${lShowAll} (${g.vehicles.length - PREVIEW})
            </button>`
        : '';

      return `<div class="tecdoc-make-group">
        <div class="tecdoc-mg-header">
          ${makeImg}
          <span class="tecdoc-mg-name">${esc(make)}</span>
          <span class="tecdoc-mg-count">${g.vehicles.length}</span>
        </div>
        <div class="tecdoc-mg-wrap">
        <table class="tecdoc-mg-table">
          <thead><tr>
            <th>${esc(make)}</th>
            <th>${lEngine}</th>
            <th>${lYear}</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        </div>
        ${moreBtn}
      </div>`;
    }).join('');

    const ptSlug    = toCollectionSlug(result.partType || '');
    const showPart  = result.partType && !ptSlug.startsWith('dichtring') && !ptSlug.startsWith('dichtsatz');
    return `
      <div class="tecdoc-vehicles-header">
        ${showPart ? `<strong>${esc(result.partType)}</strong>` : ''}
        <span class="tecdoc-vehicles-count">${result.totalVehicles} ${lCompat}</span>
      </div>
      <div class="tecdoc-make-groups" data-label-show="${lShowAll}" data-label-hide="${lCollapse}">
        ${groupHtml}
      </div>`;
  }

  function formatMoney(value, currencyCode) {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: currencyCode || 'EUR',
        maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `${amount.toFixed(2)} EUR`;
    }
  }

  function normalizeResultReference(sku) {
    const value = (sku || '').trim();
    if (!value) return '';
    return value.split('-')[0].split('_')[0].trim();
  }

  function getThemeProductCardSettings() {
    const style = window.TECDOC_PRODUCT_CARD_STYLE === 'standard' ? 'standard' : 'card';
    const colorScheme = String(window.TECDOC_PRODUCT_CARD_COLOR_SCHEME || 'scheme-4').trim() || 'scheme-4';
    return { style, colorScheme };
  }

  function renderVehicleResultsCard(item, currencyCode, options) {
    const showCompatibilityBadge = options?.showCompatibilityBadge !== false;
    const cardSettings = getThemeProductCardSettings();
    const cardClass = [
      'card',
      `card--${cardSettings.style}`,
      'card--media',
      cardSettings.style === 'card' ? `color-${cardSettings.colorScheme}` : '',
      cardSettings.style === 'card' ? 'gradient' : '',
      'card--extend-height',
      'tecdoc-results-card-core'
    ].filter(Boolean).join(' ');
    const innerClass = [
      'card__inner',
      cardSettings.style === 'standard' ? `color-${cardSettings.colorScheme}` : '',
      cardSettings.style === 'standard' ? 'gradient' : '',
      'ratio'
    ].filter(Boolean).join(' ');
    const href = item.productUrl || item.ProductUrl || item.url || item.Url || '';
    const linkAttrs = href ? `href="${esc(href)}"` : 'aria-disabled="true"';
    const vendorBadge = renderVendorBadge(item.vendor);
    const media = item.imageUrl
      ? `<div class="card__media tecdoc-results-card-media">
          <div class="media media--transparent media--hover-effect">
            <img src="${esc(item.imageUrl)}" alt="${esc(item.title)}" loading="lazy" decoding="async" class="motion-reduce" width="533" height="533" sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)">
          </div>
          ${vendorBadge}
        </div>`
      : `<div class="card__media tecdoc-results-card-media">${vendorBadge}</div>`;
    const reference = item.vehicleReference || normalizeResultReference(item.sku);
    const meta = [item.vendor, reference].filter(Boolean).join(' | ');
    const vehicleLine = [
      [item.vehicleMake, ''],
      [item.vehicleModel, ''],
      [item.vehiclePlatform, '(', ')'],
      [item.vehicleEngine, '']
    ].reduce((parts, [value, prefix, suffix]) => {
      if (!value) return parts;
      const safeValue = esc(value);
      if (prefix === '(') parts.push(`(${safeValue})`);
      else parts.push(safeValue);
      return parts;
    }, []).join(' ');
    const powerYearLine = [
      item.vehiclePowerHp ? `${esc(item.vehiclePowerHp)} PS` : '',
      item.vehicleYearFrom
        ? `${esc(item.vehicleYearFrom)}${item.vehicleYearTo && item.vehicleYearTo !== item.vehicleYearFrom ? `-${esc(item.vehicleYearTo)}` : ''}`
        : ''
    ].filter(Boolean).join(' | ');
    const badge = showCompatibilityBadge ? '<span class="tecdoc-results-card-badge">Passend für Ihr Fahrzeug</span>' : '';

    return `<li class="grid__item tecdoc-results-card">
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="${cardClass}" style="--ratio-percent: 100%;">
          <div class="${innerClass}" style="--ratio-percent: 100%;">
            ${media}
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a ${linkAttrs} class="full-unstyled-link">${esc(item.title || '')}</a>
                </h3>
              </div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <a ${linkAttrs} class="full-unstyled-link">${esc(item.title || '')}</a>
              </h3>
              <div class="card-information tecdoc-results-card-info">
                ${meta ? `<div class="caption-with-letter-spacing light">${esc(meta)}</div>` : ''}
                ${item.isVehiclePage && vehicleLine ? `<div class="caption-large light">${vehicleLine}</div>` : ''}
                ${item.isVehiclePage && powerYearLine ? `<div class="caption light">${powerYearLine}</div>` : ''}
                <div class="price tecdoc-results-card-price"><div class="price__container"><div class="price__regular"><span class="price-item price-item--regular">${formatMoney(item.price, currencyCode)}</span></div></div></div>
                ${badge}
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>`;
  }

  function renderVehicleResultsPagination(currentPage, totalPages, baseParams) {
    return renderVehicleResultsPaginationClean(currentPage, totalPages, baseParams);
  }

  function renderVehicleResultsPaginationClean(currentPage, totalPages, baseParams) {
    if (totalPages <= 1) return '';
    const caretIcon = '<span class="svg-wrapper" aria-hidden="true"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></span>';

    function buildPageUrl(page) {
      const url = new URL(window.location.pathname, window.location.origin);
      Object.entries(baseParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.set(key, String(value));
        }
      });
      url.searchParams.set('page', String(page));
      return `${url.pathname}${url.search}`;
    }

    const prevLink = currentPage > 1
      ? `<li><a href="${esc(buildPageUrl(currentPage - 1))}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Zur\u00fcck">${caretIcon}</a></li>`
      : '';
    const nextLink = currentPage < totalPages
      ? `<li><a href="${esc(buildPageUrl(currentPage + 1))}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Weiter">${caretIcon}</a></li>`
      : '';

    const items = [];
    for (let page = 1; page <= totalPages; page++) {
      if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1) {
        if (items.length && page - items[items.length - 1] > 1) items.push('ellipsis');
        items.push(page);
      }
    }

    const pageLinks = items.map((page) => {
      if (page === 'ellipsis') return '<li><span class="pagination__item">...</span></li>';
      if (page === currentPage) {
        return `<li><span class="pagination__item pagination__item--current light" aria-current="page">${page}</span></li>`;
      }
      return `<li><a href="${esc(buildPageUrl(page))}" class="pagination__item link">${page}</a></li>`;
    }).join('');

    return `<ul class="pagination__list list-unstyled tecdoc-pagination-list" role="list">${prevLink}${pageLinks}${nextLink}</ul>`;
  }


  // -- Helpers ---------------------------------------------------------------

  // -- Init ------------------------------------------------------------------
  // -- Collection vehicle filter + dynamic spec filters ----------------------
  async function initCollectionFilter() {
    const el = document.getElementById('tecdoc-collection-filter');
    if (!el) return;

    const productSel = el.dataset.productSelector || '.product-card-wrapper';
    const skuAttr    = el.dataset.skuAttr  || 'data-product-sku';
    const tBanner    = el.dataset.tBanner  || 'Gefiltert für {vehicle}';
    const tCount     = el.dataset.tCount   || '{n} kompatible Produkte';
    const tNone      = el.dataset.tNone    || 'Keine kompatiblen Produkte gefunden.';
    const tClear     = el.dataset.tClear   || 'Filter entfernen';
    const tLoading   = el.dataset.tLoading || 'Wird gefiltert...';
    const tFits      = el.dataset.tFits    || 'Passt zu Ihrem Fahrzeug';

    let _vehicleCompatSkus = null;

    function cardSku(card) {
      return (card.getAttribute(skuAttr) || '').split('-')[0].split('_')[0].trim().toUpperCase();
    }

    function applyVisibility() {
      const cards = document.querySelectorAll(productSel);
      let visible = 0;
      cards.forEach(card => {
        const ok = !_vehicleCompatSkus || _vehicleCompatSkus.has(cardSku(card));
        const item = card.closest('.grid__item') || card;
        let badge = card.querySelector('.tecdoc-cf-card-badge');
        if (_vehicleCompatSkus && ok) {
          if (!badge) {
            badge = document.createElement('div');
            badge.className = 'tecdoc-cf-card-badge';
            card.appendChild(badge);
          }
          badge.textContent = tFits;
        } else if (badge) {
          badge.remove();
        }
        card.dataset.tecdocCompat = ok ? '1' : '0';
        item.dataset.tecdocCompat = ok ? '1' : '0';
        item.style.display = ok ? '' : 'none';
        if (ok) visible++;
      });
      document.dispatchEvent(new CustomEvent('tecdoc:collection-compatibility-updated'));
      return visible;
    }

    async function applyVehicleFilter(vehicle) {
      if (!vehicle) { clearFilter(); return; }

      el.innerHTML = `<div class="tecdoc-cf-banner tecdoc-cf-banner--loading">
        <div class="tecdoc-cf-title"><p>${esc(tLoading)}</p></div>
      </div>`;

      try {
        const res  = await fetch(`${API_BASE}/compatible-skus?vehicleId=${encodeURIComponent(vehicle.id)}`);
        if (!res.ok) throw new Error(`API error ${res.status}`);
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'API error');
        const data = json.data || {};
        _vehicleCompatSkus = new Set((data.skus || []).map(s => s.split('-')[0].split('_')[0].trim().toUpperCase()));

        const visible = applyVisibility();
        const vehicleLabel = [vehicle.makeName, vehicle.modelName, vehicle.typeName].filter(Boolean).join(' ');

        el.innerHTML = `<div class="tecdoc-cf-banner">
          <div class="tecdoc-cf-title">
            <p>${esc(tBanner.replace('{vehicle}', vehicleLabel))}</p>
            <span>${esc(tCount.replace('{n}', visible))}</span>
          </div>
          ${visible === 0 ? `<span class="tecdoc-cf-none">${esc(tNone)}</span>` : ''}
          <button class="tecdoc-cf-clear" onclick="window.TecDoc.clearVehicle()">${esc(tClear)}</button>
        </div>`;

      } catch (e) {
        el.innerHTML = '';
        console.error('TecDoc collection filter error:', e);
      }
    }

    function clearFilter() {
      _vehicleCompatSkus = null;
      document.querySelectorAll(productSel).forEach(c => {
        c.querySelector('.tecdoc-cf-card-badge')?.remove();
        c.dataset.tecdocCompat = '';
        const item = c.closest('.grid__item') || c;
        item.dataset.tecdocCompat = '';
        item.style.display = '';
      });
      el.innerHTML = '';
    }

    const saved = loadVehicle();
    if (saved) applyVehicleFilter(saved);

    document.addEventListener('tecdoc:vehicle-selected', e => applyVehicleFilter(e.detail));
    document.addEventListener('tecdoc:vehicle-cleared',  () => clearFilter());
  }

  function initBreadcrumb() {
    const root = document.getElementById('tecdoc-breadcrumb-root');
    if (!root) return;
    let ancestors = [];
    try { ancestors = JSON.parse(root.dataset.ancestors || '[]'); } catch(e) { return; }
    if (!ancestors.length && !root.dataset.current) return;

    const shopUrl   = root.dataset.shopUrl  || '';
    const home      = root.dataset.home     || 'Startseite';
    const current   = root.dataset.current  || '';
    const currentUrl = root.dataset.currentUrl || '';

    // Visual breadcrumb
    const nav = document.createElement('nav');
    nav.className = 'tecdoc-breadcrumb';
    nav.setAttribute('aria-label', 'Breadcrumb');
    const ol = document.createElement('ol');
    ol.className = 'tecdoc-breadcrumb__list';

    const mkItem = (text, href) => {
      const li = document.createElement('li');
      li.className = 'tecdoc-breadcrumb__item';
      if (href) {
        const a = document.createElement('a');
        a.className = 'tecdoc-breadcrumb__link';
        a.href = href;
        a.textContent = text;
        li.appendChild(a);
      } else {
        li.className += ' tecdoc-breadcrumb__item--current';
        li.setAttribute('aria-current', 'page');
        li.textContent = text;
      }
      return li;
    };

    ol.appendChild(mkItem(home, shopUrl || '/'));
    ancestors.forEach(a => ol.appendChild(mkItem(a.name, shopUrl + '/collections/' + a.handle)));
    ol.appendChild(mkItem(current, null));
    nav.appendChild(ol);
    root.replaceWith(nav);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initSelector();
    initCompatibility();
    initWidgetVehicle();
    initWidgetPartAndRefs();
    initArticleVehicles();
    initCollectionFilter();
    initBreadcrumb();
  });

  document.addEventListener('tecdoc:vehicle-selected', updateBar);
  document.addEventListener('tecdoc:vehicle-cleared',  updateBar);

  // Expose public API
  window.TecDoc = { loadVehicle, clearVehicle, checkCompatibility, search, searchHsnTsn, getArticleVehicles, getVehicleResults };

})();
